import { useState, useEffect } from 'react';
import '../../VolumeMakerV2.css';
import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { useSolana } from '../../hooks/useSolana';
import { LiquidityPoolKeysV4, Percent, WSOL } from '@raydium-io/raydium-sdk';
import { useRaydium } from '../../hooks/useRaydium';
import { useSessionGateway } from '../../hooks/useSessionGateway';
import { REMOTE_REMOVE_100_LP_TOKEN, REMOTE_REMOVE_99_9_LP_TOKEN, REMOTE_VOLUME_BUYER_SWITCH, REMOTE_VOLUME_SELLER_SWITCH } from './RapidLaunch';
import { Api } from '@sodamnfoolish/spl-factory-backend-types';
import {
    volumeMakerV2JitoTipDafault,
    volumeMakerV2MaxPriorityFeeJitoLamports,
    volumeMakerV2MaxPriorityFeeRPCLamports,
    volumeMakerV2MinPriorityFeeJitoLamports,
    volumeMakerV2MinPriorityFeeRPCLamports,
} from '../../../ConfigFees';
import { volumeMakerV2CheckPoolBalanceLpSupplyUi, volumeMakerV2CheckPoolBalanceSolUi, volumeMakerV2FeeReserve } from '../../../ConfigConstants';

export function VolumeMakerV2({
    logMessage,
    connection,
    volumeBuyWallets,
    autoAction,
    updatedAt,
    volFunder,
    deployedToken,
    receivePoolKeys,
    poolKeys,
}: {
    logMessage: (message: string, type?: 'success' | 'error' | 'warn') => void;
    connection: Connection;
    volumeBuyWallets: Keypair[];
    autoAction: string;
    updatedAt: string;
    volFunder: Keypair | undefined;
    deployedToken: string;
    receivePoolKeys: () => Promise<LiquidityPoolKeysV4>;
    poolKeys: LiquidityPoolKeysV4 | undefined;
}) {
    const { swap, getMinAmountOut } = useRaydium();
    const { getMultipleSolBalances } = useSolana();
    const { sessionClient } = useSessionGateway();
    const [useUniversalCSV, setUseUniversalCSV] = useState<boolean>(true);
    const [loopInfinitely, setLoopInfinitely] = useState<boolean>(true);

    const orderOptions = ['Sequential', 'Random'];
    const [order, setOrder] = useState<string>(orderOptions[1]);

    const [minBuyPercent, setMinBuyPercent] = useState<number>(98);
    const [maxBuyPercent, setMaxBuyPercent] = useState<number>(99);
    const [minDelay, setMinDelay] = useState<number>(500);
    const [maxDelay, setMaxDelay] = useState<number>(1500);

    const [checkLP, setCheckLP] = useState<boolean>(true);
    const [randomBuyLock, setRandomBuyLock] = useState(false);
    const [randomBuyIntervalTimer, setRandomBuyIntervalTimer] = useState<NodeJS.Timer | null>(null);

    const [sequentialCurrentIndex, setSequentialCurrentIndex] = useState<number>(0);

    const [initBalance, setInitBalance] = useState<number>(0);
    const [currentBalance, setCurrentBalance] = useState<number>(0);
    const [totalSpent, setTotalSpent] = useState<number>(0);

    const [jitoTip, setJitoTip] = useState<number>(volumeMakerV2JitoTipDafault);

    const [jitoTransaction, setJitoTransaction] = useState<boolean>(true);
    const [volumeBuySlippagePercent, setVolumeBuySlippagePercent] = useState<number>(9);

    const handleNumberChange = (setter: React.Dispatch<React.SetStateAction<number>>, min: number, max: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);
        if (value < min) value = min;
        if (value > max) value = max;
        setter(value);
    };

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);
    useEffect(() => {
        if (autoActionStarted) return;
        if (!volumeBuyWallets.length) return;
        if (!poolKeys) return;
        const timeDifference = Date.now() - new Date(updatedAt).getTime();
        const actionDetails = autoAction.split(':');
        const action = actionDetails[0];
        const minBuyPercentParam = Number(actionDetails[1]) || minBuyPercent;
        const maxBuyPercentParam = Number(actionDetails[2]) || maxBuyPercent;
        const minDelayParam = Number(actionDetails[3]) || minDelay;
        const maxDelayParam = Number(actionDetails[4]) || maxDelay;

        setMinBuyPercent(minBuyPercentParam);
        setMaxBuyPercent(maxBuyPercentParam);
        setMinDelay(minDelayParam);
        setMaxDelay(maxDelayParam);

        if (timeDifference > 30000) return;

        if (action === 'volumeBuy') {
            logMessage('Starting auto action for volume maker.');
            setAutoActionStarted(true);
            switchRandomBuy(minBuyPercentParam, maxBuyPercentParam, minDelayParam, maxDelayParam);
        }
    }, [deployedToken, autoAction, poolKeys, autoActionStarted]);

    useEffect(() => {
        const fetchTotalBalance = async () => {
            try {
                if (useUniversalCSV) {
                    const balances = await getMultipleSolBalances(
                        connection,
                        volumeBuyWallets.map((keypair) => keypair.publicKey),
                    );
                    const totalBalance = balances.reduce((acc, balance) => acc + balance, 0);
                    setCurrentBalance(totalBalance);
                    if (initBalance === 0) {
                        setInitBalance(totalBalance);
                    }
                }
            } catch (error) {
                console.log('Error fetching total balance', error);
                logMessage('Error fetching total balance', 'error');
            }
        };

        const intervalId = setInterval(fetchTotalBalance, 2000);
        return () => {
            clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volumeBuyWallets, useUniversalCSV]);

    useEffect(() => {
        if (!sessionClient) return;

        // TODO: change logic on backend to use roles (pages)

        sessionClient.on(REMOTE_VOLUME_SELLER_SWITCH, (_: boolean, callback: Api.Common.Gateway.ClientCallback) => {
            callback([]);
        });

        sessionClient.on(REMOTE_REMOVE_99_9_LP_TOKEN, (callback: Api.Common.Gateway.ClientCallback) => {
            callback([]);
        });

        sessionClient.on(REMOTE_REMOVE_100_LP_TOKEN, (callback: Api.Common.Gateway.ClientCallback) => {
            callback([]);
        });

        sessionClient.on(REMOTE_VOLUME_BUYER_SWITCH, async (enabled: boolean, callback: Api.Common.Gateway.ClientCallback) => {
            if (!deployedToken) {
                callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Error, 'Buyer mint not provided')]);
                return;
            }

            try {
                if (randomBuyLock == enabled) {
                    callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Warn, 'Buyer already switched')]);
                    return;
                }

                await new Promise<void>((resolve, reject) => {
                    switchRandomBuy(minBuyPercent, maxBuyPercent, minDelay, maxDelay).catch(reject);
                    setTimeout(resolve, 1000);
                });

                callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Info, 'Buyer switch processed')]);
            } catch (error) {
                callback([new Api.Common.Gateway.ClientLog(Api.Common.Gateway.LogLevel.Error, 'Buyer error')]);
                throw error;
            }
        });

        return () => {
            sessionClient.off(REMOTE_VOLUME_SELLER_SWITCH);
            sessionClient.off(REMOTE_REMOVE_99_9_LP_TOKEN);
            sessionClient.off(REMOTE_REMOVE_100_LP_TOKEN);
            sessionClient.off(REMOTE_VOLUME_BUYER_SWITCH);
        };
    }, [minBuyPercent, maxBuyPercent, minDelay, maxDelay, sessionClient, randomBuyIntervalTimer, randomBuyLock, deployedToken]);

    const switchRandomBuy = async (minBuyPercent: number, maxBuyPercent: number, minDelay: number, maxDelay: number) => {
        if (randomBuyIntervalTimer) {
            clearInterval(randomBuyIntervalTimer);
            setRandomBuyIntervalTimer(null);
            setRandomBuyLock(false);
        } else {
            setRandomBuyLock(true);
            const keys = poolKeys || (await receivePoolKeys());

            let sequentialIndex = sequentialCurrentIndex;
            let randomIteration = 0;
            const intervalId = setInterval(
                async () => {
                    try {
                        //sequential
                        if (order === orderOptions[0]) {
                            console.log('index', sequentialIndex);
                            const wallet = volumeBuyWallets[sequentialIndex];
                            const walletBalance = await connection.getBalance(wallet.publicKey, 'processed');
                            console.log('walletBalance', walletBalance / LAMPORTS_PER_SOL);
                            const buyValue = Math.floor(
                                ((walletBalance - volumeMakerV2FeeReserve) * (Math.random() * (maxBuyPercent - minBuyPercent) + minBuyPercent)) / 100,
                            );
                            console.log('buyValue', buyValue / LAMPORTS_PER_SOL);
                            if (!keys) {
                                logMessage('No AMM pool keys found', 'error');
                                return;
                            }
                            buySingle(connection, wallet, buyValue, keys);
                            sequentialIndex++;
                            setSequentialCurrentIndex(sequentialIndex);

                            if (sequentialIndex >= volumeBuyWallets.length) {
                                sequentialIndex = 0;
                                if (!loopInfinitely) {
                                    clearInterval(intervalId);
                                    setRandomBuyLock(false);
                                    setSequentialCurrentIndex(0);
                                    setRandomBuyIntervalTimer(null);
                                }
                            }
                        }
                        //random
                        else if (order === orderOptions[1]) {
                            const solBalances = await getMultipleSolBalances(
                                connection,
                                volumeBuyWallets.map((wallet) => wallet.publicKey),
                            );
                            //console.debug('token balances', solBalances);
                            const walletsWithPositiveBalance = volumeBuyWallets.filter((wallet, index) => solBalances[index] > volumeMakerV2FeeReserve);

                            //console.debug('Wallets with positive balance', walletsWithPositiveBalance);
                            if (walletsWithPositiveBalance.length == 0) {
                                console.warn('No wallets with positive balance');
                                logMessage('No wallets with positive balance left', 'error');
                                return;
                            }
                            const randomIndex = Math.floor(Math.random() * (walletsWithPositiveBalance.length - 1));
                            //console.debug('Random index', randomIndex);
                            const randomWallet = walletsWithPositiveBalance[randomIndex];
                            const randomWalletBalance = await connection.getBalance(randomWallet.publicKey, 'processed');
                            //console.debug('Random wallet', randomWallet.publicKey.toBase58());
                            const buyValue = Math.floor(
                                ((randomWalletBalance - volumeMakerV2FeeReserve) * (Math.random() * (maxBuyPercent - minBuyPercent) + minBuyPercent)) / 100,
                            );
                            if (!keys) {
                                logMessage('No AMM pool keys found', 'error');
                                return;
                            }
                            buySingle(connection, randomWallet, buyValue, keys);
                            randomIteration++;
                            if (randomIteration >= volumeBuyWallets.length) {
                                if (!loopInfinitely) {
                                    clearInterval(intervalId);
                                    setRandomBuyLock(false);
                                    setRandomBuyIntervalTimer(null);
                                }
                            }
                        }
                    } catch (error) {
                        console.log('Error with buy', error);
                        logMessage('Error with buy', 'error');
                    }
                },
                minDelay + Math.random() * (maxDelay - minDelay),
            );

            setRandomBuyIntervalTimer(intervalId);

            logMessage('Random Buy enabled', 'success');
        }
    };

    async function buySingle(connection: Connection, wallet: Keypair, solAmount: number, poolKeys: LiquidityPoolKeysV4) {
        console.log('Buying', solAmount / LAMPORTS_PER_SOL, 'SOL from', wallet.publicKey.toBase58());
        try {
            if (checkLP) {
                let poolSolBalance: number;
                if (poolKeys.baseMint.toBase58() === WSOL.mint) {
                    poolSolBalance = await connection.getBalance(poolKeys.baseVault, 'processed');
                } else if (poolKeys.quoteMint.toBase58() === WSOL.mint) {
                    poolSolBalance = await connection.getBalance(poolKeys.quoteVault, 'processed');
                } else {
                    return logMessage('No sol detected', 'error');
                }
                const lpTokenSupplyUi = (await connection.getTokenSupply(poolKeys.lpMint, 'processed')).value.uiAmount;
                console.log('poolSolBalance', poolSolBalance);
                console.log('lpTokenSupplyUi', lpTokenSupplyUi);
                if (poolSolBalance < volumeMakerV2CheckPoolBalanceSolUi) {
                    logMessage(`Pool balance is less than 1 SOL. Skipping...`, 'error');
                    //switchRandomBuy();
                    return;
                }
                if (lpTokenSupplyUi! < volumeMakerV2CheckPoolBalanceLpSupplyUi) {
                    logMessage(`LP token supply is less than 1000. Skipping...`, 'error');
                    //switchRandomBuy();
                    return;
                }
            }

            const min = jitoTransaction ? volumeMakerV2MinPriorityFeeJitoLamports : volumeMakerV2MinPriorityFeeRPCLamports;
            const max = jitoTransaction ? volumeMakerV2MaxPriorityFeeJitoLamports : volumeMakerV2MaxPriorityFeeRPCLamports;
            const priorityFeeLamports = Math.floor(Math.random() * (max - min) + min);
            const minAmountOut = await getMinAmountOut(
                connection,
                solAmount,
                poolKeys,
                new PublicKey(WSOL.mint),
                new PublicKey(deployedToken),
                new Percent(volumeBuySlippagePercent),
            );

            console.log('minAmountOut', minAmountOut);
            const minAmountOutNumber = Math.floor(Number(minAmountOut.toSignificant()) * 10 ** (minAmountOut.currency.decimals + 1));
            console.log('minAmountOutNumber', minAmountOutNumber);
            console.log('solAmount', solAmount);
            swap(
                connection,
                volFunder!,
                wallet,
                poolKeys,
                solAmount,
                minAmountOutNumber,
                new PublicKey(WSOL.mint),
                new PublicKey(deployedToken),
                priorityFeeLamports,
                jitoTip * LAMPORTS_PER_SOL,
                logMessage,
                true,
                jitoTransaction,
            )
                .then(() => {
                    logMessage(`Bought ${(solAmount / LAMPORTS_PER_SOL).toFixed(3)} SOL from ${wallet.publicKey.toBase58()}`, 'success');

                    setTotalSpent((prev) => prev + solAmount);
                })
                .catch((error) => {
                    console.error(error);
                });
        } catch (error) {
            logMessage(`Error buying wallet from ${wallet.publicKey.toBase58()}`);
            console.log('Error buying wallet from', wallet.publicKey.toBase58());
            console.error(error);
        }
    }

    return (
        <section>
            <section className='csv'>
                <div className='universal-csv-enable'>
                    <input type='checkbox' checked={useUniversalCSV} onChange={() => setUseUniversalCSV(!useUniversalCSV)} disabled={true || randomBuyLock} />
                    Universal CSV
                    <input type='file' accept='.csv' id='fileInput' disabled={true || randomBuyLock} />
                    <label className='file-input-label' htmlFor='fileInput'>
                        📁 <span>Upload a CSV</span>
                    </label>
                </div>
                <div className='order'>
                    Order
                    <label className='radio'>
                        <input
                            type='radio'
                            name='mode'
                            value='Sequential'
                            checked={order === orderOptions[0]}
                            onChange={() => setOrder(orderOptions[0])}
                            disabled={randomBuyLock}
                        />
                        <span>&nbsp;&nbsp;Sequential</span>
                    </label>
                    <label className='radio'>
                        <input
                            type='radio'
                            name='mode'
                            value='Random'
                            checked={order === orderOptions[1]}
                            onChange={() => setOrder(orderOptions[1])}
                            disabled={randomBuyLock}
                        />
                        <span>&nbsp;&nbsp;Random</span>
                    </label>
                </div>
                <div className='example-btn'>
                    <a href='#' className='example-csv-link'>
                        Example CSV
                    </a>
                </div>
            </section>
            <section className='buy-range-time-delay'>
                <div className='buy-range'>
                    Buy Range
                    <input type='number' value={minBuyPercent} onChange={handleNumberChange(setMinBuyPercent, 0, 100)} disabled={randomBuyLock} />
                    to
                    <input type='number' value={maxBuyPercent} onChange={handleNumberChange(setMaxBuyPercent, 0, 100)} disabled={randomBuyLock} />%
                </div>
                <div className='time-delay'>
                    Time Delay
                    <input type='number' value={minDelay} onChange={handleNumberChange(setMinDelay, 0, Number.MAX_VALUE)} disabled={randomBuyLock} />
                    to
                    <input type='number' value={maxDelay} onChange={handleNumberChange(setMaxDelay, 0, Number.MAX_VALUE)} disabled={randomBuyLock} />
                    ms
                </div>
                <div className='jito-tip'>
                    JitoTip
                    <input
                        type='number'
                        value={jitoTip}
                        onChange={(e) => {
                            setJitoTip(Number(e.target.value));
                        }}
                    />
                </div>
            </section>
            <section className='inputs'>
                <div className='loop-check'>
                    <div className='name-check-inp-case'>
                        <div className='loop-inp-check'>
                            <input type='checkbox' checked={loopInfinitely} onChange={() => setLoopInfinitely(!loopInfinitely)} disabled={randomBuyLock} />
                            <p>Loop Infinitely</p>
                        </div>
                        <div className='lp-protection-check-inp'>
                            <input type='checkbox' checked={checkLP} onChange={() => setCheckLP(!checkLP)} disabled={randomBuyLock} />
                            <p>Check LP Protection</p>
                        </div>
                        <div className='jito-tx-check-inp'>
                            <input type='checkbox' checked={jitoTransaction} onClick={() => setJitoTransaction((prev) => !prev)} />
                            <p>Jito TX</p>
                        </div>
                    </div>
                    <div className='slip-name-inp'>
                        <p>Slippage</p>
                        <input value={volumeBuySlippagePercent} onChange={(e) => setVolumeBuySlippagePercent(Number(e.target.value))} />
                    </div>
                </div>
            </section>
            <section className='switch-precreate'>
                <label>
                    <div className='switch'>
                        <input
                            type='checkbox'
                            checked={randomBuyLock}
                            onClick={() => switchRandomBuy(minBuyPercent, maxBuyPercent, minDelay, maxDelay)}
                            // onChange={({ target: { checked } }: { target: { checked: boolean } }) => setRandomBuyLock(checked)}
                            disabled={!deployedToken || !volFunder || !volumeBuyWallets.length}
                        />
                        <span className='slider round'></span>
                        Pause/Start
                    </div>
                </label>
                <div className='precreate'>
                    <button className='button-accent'>Precreate ATA</button>
                </div>
                <div className='info'>
                    <div>{volumeBuyWallets.length} Buy Volume Wallets Detected</div>
                    <div className='text'>Initial: {(initBalance / LAMPORTS_PER_SOL).toFixed(2)}</div>
                    <div className='text'>Current: {(currentBalance / LAMPORTS_PER_SOL).toFixed(2)}</div>
                    <div className='text'>Spent: {(totalSpent / LAMPORTS_PER_SOL).toFixed(2)}</div>
                </div>
            </section>
        </section>
    );
}
